import {select, Store} from '@ngrx/store';
import {Component, HostBinding, OnDestroy, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {AuthService, GoogleAnalyticsService, GroupService, OrgService} from '../core';
import {Group, TemperatureUnit} from '../shared';

import {IDailyScoreAvg, IWeeklyAvg, ReportService, WeeklyReport} from './shared';
import * as fromRoot from '../reducers';
import * as fromAfbApi from '../afb-api/reducers';

import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import * as moment from 'moment';
import {OrganizationWithToken} from '../core/models/core';
import {filter} from 'rxjs/operators';
import {SelfOrganizationUser} from '../afb-api/models/organization-user';
import {Device} from '../afb-api/models/device';
import {skip} from 'rxjs/internal/operators';
import {HorizontalAlignment} from '../vision/dropdown/enums/dropdown.enum';

@Component({
  selector: 'awair-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss']
})

export class ReportComponent implements OnInit, OnDestroy {
  @HostBinding('style.background-color') bgColor = '#F8F9FA';
  @HostBinding('style.width') width = '100%';
  @HostBinding('style.height') height = '100%';
  @HostBinding('style.display') display = 'block';

  selectedOrganization$: Observable<OrganizationWithToken> = this.rootStore.pipe(
    select(fromRoot.selectedOrganization),
    filter((selectedOrganization: OrganizationWithToken) => selectedOrganization != null)
  );
  selfOrganizationUser$: Observable<SelfOrganizationUser> = this.afbApiStore.pipe(
    select(fromAfbApi.selfOrganizationUser),
    filter((selfOrganizationUser: SelfOrganizationUser) => selfOrganizationUser != null)
  );

  // Weekly Report variables
  scoreWeeklyAvg: IWeeklyAvg;
  scoreCondition: string;
  weeklyAvgList: IWeeklyAvg[];
  dailyScoreAvgList: IDailyScoreAvg[];
  worstFactor: string;
  colorConditionMap = {
    'good': '#4FCC53',
    'fair': '#F8A91C',
    'poor': '#E63740'
  };
  hasReport: boolean = false;

  // Dates
  since: Date;

  // Miscellaneous
  loading: boolean = true;

  // NgRx
  groupList$: Observable<Group[]> = this.rootStore.pipe(
    select(fromRoot.getCoreGroupList)
  );
  selectedGroup: Group = null;
  dateRange: { fromDate: Date, toDate: Date } = {
    fromDate: moment().subtract(1, 'w').startOf('isoWeek').toDate(),
    toDate: moment().subtract(1, 'w').endOf('isoWeek').toDate()
  };

  HorizontalAlignment = HorizontalAlignment;

  private generateReportSubs: Subscription;
  private deviceList$: Observable<Device[]>;
  private selectedGroup$ = new BehaviorSubject<Group>(this.selectedGroup);
  private dateRange$ = new BehaviorSubject<{ fromDate: Date, toDate: Date }>(this.dateRange);

  constructor(private rootStore: Store<fromRoot.State>,
              private afbApiStore: Store<fromAfbApi.State>,
              private authService: AuthService,
              private reportService: ReportService,
              private orgService: OrgService,
              private groupService: GroupService,
              private googleAnalyticsService: GoogleAnalyticsService) {
  }

  ngOnInit() {
    // Previous week of Weekly Report release date
    this.since = new Date('2017-08-14');
    this.deviceList$ = this.afbApiStore.pipe(
      select(fromAfbApi.devices),
      skip(1)
    );
    this.generateReportSubs = combineLatest(this.selectedOrganization$, this.selfOrganizationUser$,
      this.deviceList$, this.selectedGroup$, this.dateRange$,
      (selectedOrganization: OrganizationWithToken, selfOrganizationUser: SelfOrganizationUser, deviceList: Device[],
       selectedGroup: Group, dateRange: { fromDate: Date, toDate: Date }) => {
        const selectedGroupedDevice = this.groupService.getSelectedGroupedDevice(deviceList, selectedGroup);
        const hasAwair = selectedGroupedDevice.hasAwair;
        const hasMint = selectedGroupedDevice.hasMint;
        const hasOmni = selectedGroupedDevice.hasOmni;
        this.selectedGroup = selectedGroup;
        this.dateRange = dateRange;
        this.generateReport(selectedOrganization.token, selfOrganizationUser.tempUnit, dateRange.fromDate,
          dateRange.toDate, selectedGroup, hasAwair, hasMint, hasOmni);
      }).subscribe();
  }

  ngOnDestroy() {
    if (this.generateReportSubs) {
      this.generateReportSubs.unsubscribe();
    }
  }

  onSelectWeek(dateRange: { fromDate: Date, toDate: Date }): void {
    this.dateRange$.next({
      fromDate: new Date(dateRange.fromDate),
      toDate: new Date(dateRange.toDate)
    });
    this.googleAnalyticsService.emitEvent('Report', 'change - week');
  }

  selectGroup(selectedGroup: Group): void {
    this.selectedGroup$.next(selectedGroup != null ? {...selectedGroup} : null)
  }

  private generateReport(organizationToken: string, temperatureUnit: TemperatureUnit, fromDate: Date, toDate: Date, selectedGroup?: Group,
                         hasAwair?: boolean, hasMint?: boolean, hasOmni?: boolean): void {
    this.loading = true;
    this.reportService.requestReport(organizationToken, toDate, selectedGroup != null ? selectedGroup.id : null).subscribe(
      (weeklyReport: WeeklyReport | any) => {
        if (weeklyReport.message != null) {
          this.hasReport = false;
          this.loading = false;
        } else {
          const wholeWeekAvg = this.reportService.getWeeklyAvgList(weeklyReport, temperatureUnit,
            hasAwair, hasMint, hasOmni);

          this.scoreWeeklyAvg = wholeWeekAvg.shift();
          // Report is present if week score average is a number
          this.hasReport = this.scoreWeeklyAvg.value != null;
          this.scoreCondition = this.colorConditionMap[this.scoreWeeklyAvg.condition] || '#CDD3D8';
          this.weeklyAvgList = [...wholeWeekAvg];
          this.worstFactor = this.reportService.getWorstFactor(weeklyReport);

          this.dailyScoreAvgList = this.reportService.getDailyScoreAvgList(weeklyReport, fromDate);

          this.loading = false;
        }
      },
      (error: HttpErrorResponse) => {
        this.loading = false;
        if (error.status === 422) {
          this.hasReport = false;
        } else {
          alert('Failed to generate weekly report.\nPlease try again later.');
        }
      }
    );
  }
}
